// src/pages/manutencoes/LeiturasPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Form, InputGroup, Row, Col,
  Modal, Spinner, Alert, Pagination, Badge
} from 'react-bootstrap';
import { FaSearch, FaPlus, FaTrash, FaExclamationTriangle, FaSave, FaCheck, FaPrint } from 'react-icons/fa';
import useSortable from '../../hooks/useSortable';
import SortableTh from '../../components/SortableTh';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import api from '../../service/api';

interface Leitura {
  LIGACAO: number;
  FICHA: number;
  NOME: string;
  ANTERIOR: number;
  ATUAL: number;
  LIG_ENDERECO?: string;
}

interface LeituraFormValues {
  LIGACAO: number;
  FICHA: number;
  NOME: string;
  ANTERIOR: number;
  ATUAL: number;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const ITEMS_PER_PAGE = 20;

const validationSchema = Yup.object({
  LIGACAO: Yup.number().required('Ligação é obrigatória').min(1),
  FICHA: Yup.number().min(0),
  NOME: Yup.string().max(100),
  ANTERIOR: Yup.number().min(0),
  ATUAL: Yup.number().min(0).test('maior-anterior', 'Deve ser >= leitura anterior', function (v) {
    return v !== undefined ? v >= this.parent.ANTERIOR : true;
  }),
});

const emptyForm: LeituraFormValues = { LIGACAO: 0, FICHA: 0, NOME: '', ANTERIOR: 0, ATUAL: 0 };

const printListaLeituras = (leituras: Leitura[]) => {
  const hoje = new Date().toLocaleDateString('pt-BR');
  const linhas = leituras.map((l, idx) => `
    <tr style="background:${idx % 2 === 0 ? '#fff' : '#f9f9f9'}">
      <td style="border:1px solid #ccc;padding:6px 8px;text-align:center">${l.FICHA}</td>
      <td style="border:1px solid #ccc;padding:6px 8px">${l.NOME}</td>
      <td style="border:1px solid #ccc;padding:6px 8px">${l.LIG_ENDERECO ?? ''}</td>
      <td style="border:1px solid #ccc;padding:6px 8px;text-align:center;font-weight:bold">${l.ATUAL}</td>
      <td style="border:1px solid #ccc;padding:6px 8px;width:110px"></td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html><html>
  <head><meta charset="UTF-8"><title>Lista de Leituras</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 16px; font-size: 12px; }
    h2 { text-align: center; margin: 0; font-size: 14px; }
    p  { text-align: center; margin: 2px 0 12px; font-size: 11px; color: #555; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #e0e0e0; border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
    @media print { @page { margin: 1cm; } }
  </style></head>
  <body>
    <h2>Sociedade Amigos de Coqueiros — Lista de Leituras</h2>
    <p>Emitido em ${hoje} &nbsp;|&nbsp; ${leituras.length} ligação(ões)</p>
    <table>
      <thead><tr>
        <th style="width:55px;text-align:center">Ficha</th>
        <th>Nome</th>
        <th>Endereço</th>
        <th style="width:90px;text-align:center">Leit. Atual</th>
        <th style="width:110px">Leit. Informada</th>
      </tr></thead>
      <tbody>${linhas}</tbody>
    </table>
    <script>window.print();</script>
  </body></html>`;

  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); }
};

const LeiturasPage: React.FC = () => {
  const [leituras, setLeituras] = useState<Leitura[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Valores em edição inline: chave = LIGACAO
  const [editValues, setEditValues] = useState<Record<number, { ANTERIOR: number; ATUAL: number }>>({});
  // Status de save por linha
  const [saveStatus, setSaveStatus] = useState<Record<number, SaveStatus>>({});

  const [showModal, setShowModal] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leituraToDelete, setLeituraToDelete] = useState<Leitura | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Modal Movimento
  const hoje = new Date();
  const defMes = String(hoje.getMonth() === 0 ? 12 : hoje.getMonth()).padStart(2, '0');
  const defAno = (hoje.getMonth() === 0 ? hoje.getFullYear() - 1 : hoje.getFullYear()).toString();
  const defData = hoje.toISOString().split('T')[0];

  const [showMovModal, setShowMovModal] = useState(false);
  const [movAno, setMovAno]           = useState(defAno);
  const [movMes, setMovMes]           = useState(defMes);
  const [movCustoM3, setMovCustoM3]   = useState('0');
  const [movTaxaM3, setMovTaxaM3]     = useState('0');
  const [movData, setMovData]         = useState(defData);
  const [movSituacao, setMovSituacao] = useState('A');
  const [movLoading, setMovLoading]   = useState(false);
  const [movMsg, setMovMsg]           = useState<{ tipo: 'success' | 'danger'; texto: string } | null>(null);

  const handleLancarZerados = async () => {
    try {
      setMovLoading(true);
      setMovMsg(null);
      const res = await api.post('/leituras/lancar-zerados', {}, { timeout: 120000 });
      setMovMsg({ tipo: 'success', texto: res.data.message });
      fetchLeituras();
    } catch (err: any) {
      const detalhe = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erro desconhecido.';
      setMovMsg({ tipo: 'danger', texto: `Erro ao lançar zerados: ${detalhe}` });
    } finally {
      setMovLoading(false);
    }
  };

  const handleGeraMovimento = async () => {
    try {
      setMovLoading(true);
      setMovMsg(null);
      const res = await api.post('/leituras/gera-movimento', {
        ano: movAno,
        mes: movMes,
        situacao: movSituacao,
        data: movData,
        custo: movCustoM3,
        taxa: movTaxaM3,
      }, { timeout: 120000 });
      setMovMsg({ tipo: 'success', texto: res.data.message });
    } catch (err: any) {
      const detalhe = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erro desconhecido.';
      setMovMsg({ tipo: 'danger', texto: `Erro ao gerar movimentos: ${detalhe}` });
    } finally {
      setMovLoading(false);
    }
  };

  const handleLimparLeituras = async () => {
    try {
      setMovLoading(true);
      setMovMsg(null);
      await api.post('/leituras/gera-leituras', { mes: movMes, ano: movAno });
      setMovMsg({ tipo: 'success', texto: `Leituras geradas com sucesso para ${movMes}/${movAno}.` });
      fetchLeituras();
    } catch (err: any) {
      const detalhe = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erro desconhecido.';
      setMovMsg({ tipo: 'danger', texto: `Erro ao executar GERA_LEITURAS: ${detalhe}` });
    } finally {
      setMovLoading(false);
    }
  };

  useEffect(() => {
    const mes = parseInt(movMes, 10);
    const ano = parseInt(movAno, 10);
    const proximoMes = mes === 12 ? 1 : mes + 1;
    const proximoAno = mes === 12 ? ano + 1 : ano;
    const data = `${proximoAno}-${String(proximoMes).padStart(2, '0')}-20`;
    setMovData(data);
  }, [movAno, movMes]);

  const abrirMovModal = async () => {
    setMovMsg(null);
    setMovLoading(true);
    setShowMovModal(true);
    try {
      const res = await api.get('/configuracao');
      const cfg = Array.isArray(res.data) ? res.data[0] : res.data;
      if (cfg) {
        setMovCustoM3(String(cfg.CFG_TAXAEXCESSO ?? '0'));
        setMovTaxaM3(String(cfg.CFG_TAXAMETROS ?? '0'));
      }
    } catch { /* mantém os valores padrão */ }
    finally { setMovLoading(false); }
  };

  const fetchLeituras = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [res, ligRes] = await Promise.all([api.get('/leituras'), api.get('/ligacoes')]);
      const enderecoMap = new Map<number, string>(
        ligRes.data.map((l: any) => [l.LIG_CODIGO, l.LIG_ENDERECO])
      );
      const sorted = [...res.data]
        .map((l: Leitura) => ({ ...l, LIG_ENDERECO: enderecoMap.get(l.LIGACAO) ?? '' }))
        .map((l: Leitura) => l);
      setLeituras(sorted);
      // Inicializa os valores de edição com os dados do banco
      const initial: Record<number, { ANTERIOR: number; ATUAL: number }> = {};
      sorted.forEach((l: Leitura) => {
        initial[l.LIGACAO] = { ANTERIOR: l.ANTERIOR, ATUAL: l.ATUAL };
      });
      setEditValues(initial);
      setSaveStatus({});
      setCurrentPage(1);
    } catch {
      setError('Não foi possível carregar as leituras.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeituras(); }, [fetchLeituras]);

  const handleFieldChange = (ligacao: number, field: 'ANTERIOR' | 'ATUAL', value: string) => {
    const num = parseInt(value, 10);
    setEditValues(prev => ({
      ...prev,
      [ligacao]: { ...prev[ligacao], [field]: isNaN(num) ? 0 : num },
    }));
    setSaveStatus(prev => ({ ...prev, [ligacao]: 'idle' }));
  };

  const handleFieldBlur = async (leitura: Leitura) => {
    const vals = editValues[leitura.LIGACAO];
    if (!vals) return;

    const changed = vals.ANTERIOR !== leitura.ANTERIOR || vals.ATUAL !== leitura.ATUAL;
    if (!changed) return;

    if (vals.ATUAL < vals.ANTERIOR) {
      setSaveStatus(prev => ({ ...prev, [leitura.LIGACAO]: 'error' }));
      return;
    }

    try {
      setSaveStatus(prev => ({ ...prev, [leitura.LIGACAO]: 'saving' }));
      await api.put(`/leituras/${leitura.LIGACAO}`, {
        ...leitura,
        ANTERIOR: vals.ANTERIOR,
        ATUAL: vals.ATUAL,
      });
      // Atualiza o registro local para o novo "original"
      setLeituras(prev => prev.map(l =>
        l.LIGACAO === leitura.LIGACAO ? { ...l, ANTERIOR: vals.ANTERIOR, ATUAL: vals.ATUAL } : l
      ));
      setSaveStatus(prev => ({ ...prev, [leitura.LIGACAO]: 'saved' }));
      setTimeout(() => setSaveStatus(prev => ({ ...prev, [leitura.LIGACAO]: 'idle' })), 2000);
    } catch {
      setSaveStatus(prev => ({ ...prev, [leitura.LIGACAO]: 'error' }));
    }
  };

  const handleSubmit = async (
    values: LeituraFormValues,
    { resetForm }: FormikHelpers<LeituraFormValues>
  ) => {
    try {
      setFormSubmitting(true);
      await api.post('/leituras', values);
      setShowModal(false);
      resetForm();
      fetchLeituras();
    } catch {
      setError('Não foi possível salvar. Tente novamente.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!leituraToDelete) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/leituras/${leituraToDelete.LIGACAO}`);
      setShowDeleteModal(false);
      setLeituraToDelete(null);
      fetchLeituras();
    } catch {
      setError('Não foi possível excluir. Tente novamente.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const filtered = leituras.filter(l =>
    l.NOME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(l.LIGACAO).includes(searchTerm) ||
    String(l.FICHA).includes(searchTerm)
  );

  const { sortedData: sortedLeituras, sortKey, sortDir, handleSort } = useSortable<Leitura>(filtered, 'NOME');
  const paginated = sortedLeituras.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const statusIndicator = (ligacao: number) => {
    const s = saveStatus[ligacao];
    if (s === 'saving') return <Spinner animation="border" size="sm" variant="primary" />;
    if (s === 'saved')  return <span className="text-success">{FaCheck({ size: 14 })}</span>;
    if (s === 'error')  return <span className="text-danger" title="Leitura atual deve ser ≥ anterior">{FaExclamationTriangle({ size: 14 })}</span>;
    return null;
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Lançamento de Leituras</h1>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => printListaLeituras(sortedLeituras)} disabled={leituras.length === 0}>
            {FaPrint({ className: 'me-2' })} Imprimir Lista
          </Button>
          <Button variant="outline-success" onClick={abrirMovModal}>
            Movimento
          </Button>
          <Button variant="primary" onClick={() => setShowModal(true)}>
            {FaPlus({ className: 'me-2' })} Nova Leitura
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{FaExclamationTriangle({ className: 'me-2' })}{error}</Alert>}

      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>{FaSearch({})}</InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por nome, ligação ou ficha..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end align-items-center gap-2">
              <span className="text-muted">Total: {filtered.length}</span>
              <Badge bg="secondary" pill>Edite os campos direto na tabela</Badge>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-3">Carregando...</p></div>
          ) : filtered.length === 0 ? (
            <Alert variant="info">Nenhuma leitura encontrada.</Alert>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover size="sm" style={{ fontSize: '14px' }}>
                  <thead className="table-light">
                    <tr>
                      <SortableTh<Leitura> label="Ligação" sortKey="LIGACAO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Leitura> label="Ficha" sortKey="FICHA" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Leitura> label="Nome" sortKey="NOME" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Leitura> label="Endereço" sortKey="LIG_ENDERECO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <th style={{ width: '120px' }}>Leit. Anterior</th>
                      <th style={{ width: '120px' }}>Leit. Atual</th>
                      <th>Consumo (m³)</th>
                      <th style={{ width: '60px' }}></th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map(l => {
                      const vals = editValues[l.LIGACAO] ?? { ANTERIOR: l.ANTERIOR, ATUAL: l.ATUAL };
                      const consumo = vals.ATUAL - vals.ANTERIOR;
                      const hasError = saveStatus[l.LIGACAO] === 'error';
                      return (
                        <tr key={l.LIGACAO}>
                          <td>{l.LIGACAO}</td>
                          <td>{l.FICHA}</td>
                          <td>{l.NOME}</td>
                          <td>{l.LIG_ENDERECO}</td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={vals.ANTERIOR}
                              onChange={e => handleFieldChange(l.LIGACAO, 'ANTERIOR', e.target.value)}
                              onBlur={() => handleFieldBlur(l)}
                              isInvalid={hasError}
                              style={{ width: '100px' }}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              value={vals.ATUAL}
                              onChange={e => handleFieldChange(l.LIGACAO, 'ATUAL', e.target.value)}
                              onBlur={() => handleFieldBlur(l)}
                              isInvalid={hasError}
                              style={{ width: '100px' }}
                            />
                          </td>
                          <td className={consumo < 0 ? 'text-danger' : ''}>{consumo} m³</td>
                          <td className="text-center">{statusIndicator(l.LIGACAO)}</td>
                          <td>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => { setLeituraToDelete(l); setShowDeleteModal(true); }}
                              title="Excluir"
                            >
                              {FaTrash({})}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {arr[idx - 1] && p - arr[idx - 1] > 1 && <Pagination.Ellipsis disabled />}
                          <Pagination.Item active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                        </React.Fragment>
                      ))}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal Nova Leitura */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nova Leitura</Modal.Title>
        </Modal.Header>
        <Formik initialValues={emptyForm} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit: fs }) => (
            <Form onSubmit={fs}>
              <Modal.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ligação *</Form.Label>
                      <Form.Control type="number" name="LIGACAO" value={values.LIGACAO} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.LIGACAO && !!errors.LIGACAO} />
                      <Form.Control.Feedback type="invalid">{errors.LIGACAO}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ficha</Form.Label>
                      <Form.Control type="number" name="FICHA" value={values.FICHA} onChange={handleChange} onBlur={handleBlur} />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Nome</Form.Label>
                  <Form.Control name="NOME" value={values.NOME} onChange={handleChange} onBlur={handleBlur} />
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Leitura Anterior</Form.Label>
                      <Form.Control type="number" name="ANTERIOR" value={values.ANTERIOR} onChange={handleChange} onBlur={handleBlur} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Leitura Atual *</Form.Label>
                      <Form.Control type="number" name="ATUAL" value={values.ATUAL} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.ATUAL && !!errors.ATUAL} />
                      <Form.Control.Feedback type="invalid">{errors.ATUAL}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                {values.ATUAL > 0 && (
                  <Alert variant="info">Consumo: <strong>{values.ATUAL - values.ANTERIOR} m³</strong></Alert>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button variant="primary" type="submit" disabled={formSubmitting}>
                  {formSubmitting ? 'Salvando...' : <>{FaSave({ className: 'me-2' })}Salvar</>}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Modal Movimento */}
      <Modal show={showMovModal} onHide={() => setShowMovModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Gerar Movimentos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {movLoading ? (
            <div className="text-center py-3"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <>
              {movMsg && <Alert variant={movMsg.tipo} dismissible onClose={() => setMovMsg(null)}>{movMsg.texto}</Alert>}
              <Row>
                <Col md={8}>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Ano</Form.Label>
                        <Form.Control value={movAno} onChange={e => setMovAno(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Mês</Form.Label>
                        <Form.Select value={movMes} onChange={e => setMovMes(e.target.value)}>
                          {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Custo M3</Form.Label>
                        <Form.Control type="number" step="0.01" value={movCustoM3} onChange={e => setMovCustoM3(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Taxa M3</Form.Label>
                        <Form.Control type="number" step="0.01" value={movTaxaM3} onChange={e => setMovTaxaM3(e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Data</Form.Label>
                        <Form.Control type="date" value={movData} onChange={e => setMovData(e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Situação</Form.Label>
                        <Form.Select value={movSituacao} onChange={e => setMovSituacao(e.target.value)}>
                          <option value="A">Aberto</option>
                          <option value="P">Pago</option>
                          <option value="C">Cancelado</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
                <Col md={4}>
                  <div className="bg-light border rounded p-3 h-100" style={{ fontSize: '12px' }}>
                    <strong>Limpar leituras:</strong> buscar as leituras do último movimento de cobranças para informar as leituras repassadas pelos clientes.
                    <br /><br />
                    Ex.: Vencimento em 20/09, o movimento de pagamentos foi de Agosto então tem que informar o mês de Julho e Ano correspondente.
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={handleLimparLeituras} disabled={movLoading}>
              {movLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
              Limpar Leituras
            </Button>
            <Button variant="outline-warning" onClick={handleLancarZerados} disabled={movLoading}>Lançar Zerados</Button>
            <Button variant="success" onClick={handleGeraMovimento} disabled={movLoading}>Gerar Movimentos</Button>
          </div>
          <Button variant="secondary" onClick={() => setShowMovModal(false)}>Fechar</Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Excluir */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmar Exclusão</Modal.Title></Modal.Header>
        <Modal.Body className="text-center py-4">
          {FaExclamationTriangle({ size: 48, className: 'text-danger mb-3' })}
          <p>Excluir leitura da ligação <strong>#{leituraToDelete?.LIGACAO}</strong>?</p>
          <p className="text-danger small">Esta ação não pode ser desfeita.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteLoading}>{deleteLoading ? 'Excluindo...' : 'Excluir'}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LeiturasPage;
